/**
 * The arithmetic setup needs to explain a lookup, ported from the engine.
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
 *
 * Two things the interface has to say cannot be asked for over the bridge. The
 * value-kind proposal and its reasons are computed at import and returned once,
 * from `import-data-table`; nothing carries them afterwards, so a table imported
 * last week has no proposal to show beside the rule being built on it today. And
 * a test that comes back "no match" arrives as that sentence and nothing else —
 * the engine answered the question it was asked, and "why not" is a different
 * question with no command behind it.
 *
 * So the normalisation, the column profiler and the similarity measure are
 * mirrored here, deliberately line-for-line against
 * `services/document-engine/LookupMatcher.cs` and `LookupColumnProfiler.cs`. A
 * second implementation is a real cost: it can drift, and a proposal that
 * disagrees with the one the engine made at import is worse than none. It is
 * taken because the alternative is an operator choosing a matching ladder with
 * no reasoning in front of them, which is the failure this rewrite exists to
 * fix. Nothing here decides anything — every match a document gets is still
 * decided by the engine. This only explains.
 *
 * If a `profile-data-table` command is ever added, delete the profiler below and
 * call it.
 */

import type { DataTableColumnProfile, LookupRuleDefinition } from '../shared/desktop-api';

export const VALUE_KIND_CODE = 'code';
export const VALUE_KIND_NAME = 'name';

export const LEGAL_SUFFIXES = ['INC', 'INCORPORATED', 'LLC', 'LTD', 'CORP'];

/**
 * The one spelling switch per value kind a rule can turn off, and what the editor calls it.
 * Turning it off makes the rule read its column's strict key (LookupMatcher.Strict).
 */
export const STRICT_SWITCH: Record<string, { id: string; label: string }> = {
  [VALUE_KIND_CODE]: { id: 'leading-zeros', label: 'Leading zeros' },
  [VALUE_KIND_NAME]: { id: 'legal-suffix', label: 'Inc / LLC / Ltd / Corp' },
};

/** Each glyph mapped to the representative of its confusion class, after upper-casing. */
const CONFUSION_REPRESENTATIVES: Record<string, string> = {
  O: '0',
  I: '1',
  L: '1',
  S: '5',
  B: '8',
  Z: '2',
  G: '6',
};

/** What a run of separators leaves behind in a code key: the boundary, not the character. */
const SEGMENT_BOUNDARY = '-';

/**
 * The character tests, matched to the .NET ones they stand in for.
 *
 * `char.IsDigit` is the decimal-digit category, not the ASCII range, and the
 * difference is not academic: a column of full-width digits off an Asian export
 * measured as 100% digits in the engine and as "almost entirely letters" in the
 * browser, which flipped the proposal and put an operator on the name ladder for
 * a column of identifiers.
 *
 * `isMatchable` is `LookupKeys.IsMatchable`: letters, *decimal* digits, and
 * combining marks, which are part of the letter in front of them. It is the
 * decimal category rather than every number category because the engine asks
 * `char.IsLetterOrDigit`, which admits `Nd` and nothing else of `N` — so `½` and
 * the Roman numeral `Ⅻ` are separators on both sides, and only after
 * `canonicalize` has already composed away the ones that have a composition.
 */
const isDigit = (character: string) => /\p{Nd}/u.test(character);
const isLetter = (character: string) => /\p{L}/u.test(character);
const isMatchable = (character: string) => /[\p{L}\p{Nd}\p{Mn}\p{Mc}\p{Me}]/u.test(character);

/** Whether a single UTF-16 unit is a Unicode format character, as `UnicodeCategory.Format` is. */
const isFormat = (character: string) => /\p{Cf}/u.test(character);

/**
 * The zero-width characters removed by name as well as by category.
 *
 * Written as code points rather than as themselves because a source file holding
 * an invisible character is a source file nobody can review. The set is the
 * engine's, and it is explicit there for the reason it is explicit here: which of
 * these the Unicode tables call a format character has moved over the years, and
 * a zero-width space classed as a space would put the defect straight back.
 */
const ZERO_WIDTH = new Set([0x200b, 0x200c, 0x200d, 0x2060, 0xfeff]);

/**
 * The decimal value of a digit, standing in for `CharUnicodeInfo.GetDecimalDigitValue`.
 *
 * Every decimal digit set in Unicode is ten consecutive code points with the zero
 * first, so how far a digit sits from the start of its own run *is* its value.
 * Counting backwards is bounded at nine, which is the whole run.
 */
const decimalDigitValue = (character: string): number => {
  const code = character.codePointAt(0) ?? 0;
  let offset = 0;
  while (offset < 9 && isDigit(String.fromCodePoint(code - offset - 1))) offset += 1;
  return offset;
};

/**
 * `string.ToUpperInvariant()`, which is not what `toUpperCase()` does.
 *
 * .NET upper-cases one character to one character and leaves alone anything with
 * no single-character uppercase; JavaScript applies the full mapping, so `ß`
 * becomes `SS` and `ﬁ` becomes `FI`. That is the one direction in which this copy
 * was *looser* than the engine, which is the dangerous one: a table holding
 * `Grosse` and an operator typing `Groß` scored 97% alike in the browser and 82%
 * in the engine, so the pane promised that switching on close spellings would
 * match it and the field still came out blank.
 *
 * Applied per code point, keeping the original wherever the uppercase is not
 * exactly one code point — which is precisely .NET's simple case mapping.
 */
export const toUpperInvariant = (value: string): string => {
  let built = '';
  for (const character of value) {
    const upper = character.toUpperCase();
    built += [...upper].length === 1 ? upper : character;
  }
  return built;
};

/**
 * `LookupKeys.Canonicalize`: everything that has to be true of a value before two
 * of them can be compared at all.
 *
 * This was the largest thing missing from this copy, and every key below is built
 * on it in the engine. Without it the browser and the engine key the same string
 * differently on any value carrying an invisible character, a decomposed accent, a
 * full-width letter or a non-ASCII digit — so a pane whose whole purpose is to say
 * what the engine will do said something else. See the C# original for the three
 * classes of real text this covers; the tests hold both copies to the same
 * strings.
 *
 * Iterated by UTF-16 unit rather than by code point, because the engine's
 * `foreach (var character in value)` is, and a lone surrogate must be classified
 * the same way on both sides.
 */
export function canonicalize(value: string | null | undefined): string {
  if (!value) return '';
  let visible = '';
  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    // Format characters carry no glyph, so they cannot be what separates two
    // segments an operator can see. The explicit set is the engine's, and is there
    // because which of these the Unicode tables call a format character has moved.
    if (isFormat(character)) continue;
    if (ZERO_WIDTH.has(character.charCodeAt(0))) continue;
    visible += character;
  }

  let composed = visible;
  try {
    composed = visible.normalize('NFKC');
  } catch {
    // Not well-formed Unicode. Nothing can be composed, and refusing to compare it
    // at all would be worse than comparing what actually arrived.
  }

  let built = '';
  for (let index = 0; index < composed.length; index += 1) {
    const character = composed[index];
    if (isDigit(character)) {
      built += String(decimalDigitValue(character));
      continue;
    }
    // The Turkish pair. Invariant upper-casing leaves both where they are, so a
    // vendor file exported on a Turkish workstation never meets its own readings.
    built += character === 'ı' || character === 'İ' ? 'I' : character;
  }
  return built;
}

/** The engine's `Canonicalize(value).ToUpperInvariant()`, which begins every key it builds. */
const canonicalUpper = (value: string): string => toUpperInvariant(canonicalize(value));

/**
 * Words, counted the way the engine counts them.
 *
 * `Split(' ')` — one space, and nothing else. Splitting on `\s+` instead made a
 * tab-separated cell like `PART\tA` two words in the browser and one in the
 * engine, which is a whole weight in the wrong column: a table of part codes
 * proposed as names, with "Values average 2.0 words." given as the reason.
 */
const countWords = (value: string): number =>
  value
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length > 0).length;

export const normalizeValueKind = (valueKind: string | null | undefined): string =>
  (valueKind ?? '').trim().toLowerCase() === VALUE_KIND_CODE ? VALUE_KIND_CODE : VALUE_KIND_NAME;

/**
 * Case folded, separators reduced to a boundary, and zero-padding removed where
 * it is padding — the engine's `NormalizeCode`, including the two rules that stop
 * `12-3` and `1-23`, or `A01` and `A1`, landing on one key.
 */
export function normalizeCode(
  value: string | null | undefined,
  stripSegmentPadding = true,
): string {
  if (!value || !value.trim()) return '';
  const upper = canonicalUpper(value);
  let built = '';
  let pendingBoundary = false;
  let atSegmentStart = true;
  let index = 0;
  while (index < upper.length) {
    const character = upper[index];
    if (!isMatchable(character)) {
      pendingBoundary = built.length > 0;
      atSegmentStart = true;
      index += 1;
      continue;
    }
    if (pendingBoundary) {
      built += SEGMENT_BOUNDARY;
      pendingBoundary = false;
    }
    if (isDigit(character)) {
      let runEnd = index;
      while (runEnd < upper.length && isDigit(upper[runEnd])) runEnd += 1;
      const run = upper.slice(index, runEnd);
      // Folding a confusable letter into a digit lengthens the digit run, and
      // stripping leading zeros on top of that forgives a whole character's
      // PRESENCE rather than its identity — which is how AC-B12 came to match a
      // table holding only AC-0B12. The confusion key therefore does not strip.
      const stripped = atSegmentStart && stripSegmentPadding ? run.replace(/^0+/, '') : run;
      built += stripped.length === 0 ? run : stripped;
      index = runEnd;
      atSegmentStart = false;
      continue;
    }
    built += character;
    atSegmentStart = false;
    index += 1;
  }
  return built;
}

/** Case, punctuation, whitespace and (unless told not to) trailing legal suffixes removed. */
export function normalizeName(value: string | null | undefined, stripLegalSuffixes = true): string {
  if (!value || !value.trim()) return '';
  const tokens: string[] = [];
  let token = '';
  for (const character of canonicalUpper(value)) {
    if (isMatchable(character)) token += character;
    else if (token.length > 0) {
      tokens.push(token);
      token = '';
    }
  }
  if (token.length > 0) tokens.push(token);
  if (tokens.length === 0) return '';
  while (
    stripLegalSuffixes &&
    tokens.length > 1 &&
    LEGAL_SUFFIXES.includes(tokens[tokens.length - 1])
  )
    tokens.pop();
  return tokens.join(' ');
}

/** Replaces every member of the confusion set with its class representative. */
export function fold(value: string | null | undefined): string {
  if (!value) return '';
  // rn before the per-character pass, because it is the one substitution that
  // changes length and would otherwise be hidden behind the letters it contains.
  // Canonicalised first so a full-width O is folded onto a zero like any other.
  const upper = canonicalUpper(value).split('RN').join('M');
  let built = '';
  for (const character of upper) built += CONFUSION_REPRESENTATIVES[character] ?? character;
  return built;
}

export const exactKey = (value: string | null | undefined, valueKind: string): string =>
  normalizeValueKind(valueKind) === VALUE_KIND_CODE ? normalizeCode(value) : normalizeName(value);

/** The engine's `StrictKey`: the exact key with nothing trimmed (no padding off codes, no suffix off names). */
export const strictKey = (value: string | null | undefined, valueKind: string): string =>
  normalizeValueKind(valueKind) === VALUE_KIND_CODE
    ? normalizeCode(value, false)
    : normalizeName(value, false);

/** Whether a rule has its kind's strict switch off, and so reads the strict key (the engine's `Strict`). */
export const readsStrictKey = (rule: LookupRuleDefinition): boolean =>
  rule.matchLadder.normalisation?.[STRICT_SWITCH[normalizeValueKind(rule.valueKind)].id] === false;

export const confusionKey = (value: string | null | undefined, valueKind: string): string =>
  normalizeValueKind(valueKind) === VALUE_KIND_CODE
    ? normalizeCode(fold(value), false)
    : fold(normalizeName(value));

/** Jaro-Winkler similarity in [0,1] over two already-normalised strings. */
export function jaroWinkler(left: string, right: string): number {
  if (left.length === 0 || right.length === 0) return left.length === right.length ? 1 : 0;
  if (left === right) return 1;

  const window = Math.max(0, Math.floor(Math.max(left.length, right.length) / 2) - 1);
  const leftMatched = new Array<boolean>(left.length).fill(false);
  const rightMatched = new Array<boolean>(right.length).fill(false);
  let matches = 0;
  for (let i = 0; i < left.length; i += 1) {
    const start = Math.max(0, i - window);
    const end = Math.min(i + window + 1, right.length);
    for (let j = start; j < end; j += 1) {
      if (rightMatched[j] || left[i] !== right[j]) continue;
      leftMatched[i] = true;
      rightMatched[j] = true;
      matches += 1;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < left.length; i += 1) {
    if (!leftMatched[i]) continue;
    while (!rightMatched[k]) k += 1;
    if (left[i] !== right[k]) transpositions += 1;
    k += 1;
  }
  const jaro =
    (matches / left.length + matches / right.length + (matches - transpositions / 2) / matches) / 3;

  let prefix = 0;
  const prefixLimit = Math.min(4, left.length, right.length);
  while (prefix < prefixLimit && left[prefix] === right[prefix]) prefix += 1;
  return jaro + prefix * 0.1 * (1 - jaro);
}

const percent = (ratio: number) => `${Math.round(ratio * 100)}%`;

/** The longest prefix of at least two characters shared by most values, if there is one. */
function sharedPrefix(values: string[]): { prefix: string | null; share: number } {
  const upper = values.map((value) => value.toUpperCase());
  const reference = upper[0];
  for (let length = Math.min(8, reference.length); length >= 2; length -= 1) {
    const prefix = reference.slice(0, length);
    const share = upper.filter((value) => value.startsWith(prefix)).length / upper.length;
    if (share >= 0.6) return { prefix, share };
  }
  return { prefix: null, share: 0 };
}

const endsInLegalSuffix = (value: string): boolean => {
  const tokens = value
    .toUpperCase()
    .split(/[ ,.\t]+/)
    .filter((token) => token.length > 0);
  return tokens.length > 1 && LEGAL_SUFFIXES.includes(tokens[tokens.length - 1]);
};

/**
 * Proposes whether a column holds codes or names, from the column itself.
 *
 * Arithmetic rather than a model call, and every signal comes back out as a
 * sentence, because the proposal decides which ladder a rule climbs and "the
 * model said so" is not something an operator can argue with.
 */
// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function profileColumn(column: string, sampledValues: string[]): DataTableColumnProfile {
  const values = sampledValues
    .filter((value) => value && value.trim().length > 0)
    .map((value) => value.trim());
  if (values.length === 0) {
    return {
      column,
      proposedValueKind: VALUE_KIND_NAME,
      confidence: 0,
      reasons: [],
      contrary: ['The column is empty, so there was nothing to read.'],
      profiledRowCount: sampledValues.length,
      nonEmptyCount: 0,
      distinctCount: 0,
      meanTokenCount: 0,
      whitespaceRatio: 0,
      digitRatio: 0,
      lengthVariation: 0,
      uniqueRatio: 0,
      sharedPrefix: null,
    };
  }

  // Counted the way `Distinct(StringComparer.OrdinalIgnoreCase)` counts, which is the
  // simple case mapping: `toUpperCase` merges `Straße` onto `Strasse` and the engine
  // does not, and one row of difference in the unique ratio is a whole weight — enough
  // to flip the proposal and put an operator on the other ladder.
  const distinct = new Set(values.map(toUpperInvariant)).size;
  const uniqueRatio = distinct / values.length;
  const meanTokens = values.reduce((total, value) => total + countWords(value), 0) / values.length;
  const whitespaceRatio = values.filter((value) => /\s/.test(value)).length / values.length;
  const totalCharacters = values.reduce((total, value) => total + value.length, 0);
  const digitCharacters = values.reduce(
    (total, value) => total + [...value].filter(isDigit).length,
    0,
  );
  const digitRatio = totalCharacters === 0 ? 0 : digitCharacters / totalCharacters;
  const mixedRatio =
    values.filter((value) => [...value].some(isLetter) && [...value].some(isDigit)).length /
    values.length;
  const meanLength = totalCharacters / values.length;
  const lengthVariation =
    meanLength <= 0
      ? 0
      : Math.sqrt(
          values.reduce((total, value) => total + (value.length - meanLength) ** 2, 0) /
            values.length,
        ) / meanLength;
  const suffixRatio = values.filter(endsInLegalSuffix).length / values.length;
  const prefix = sharedPrefix(values);

  const forCode: { weight: number; reason: string }[] = [];
  const forName: { weight: number; reason: string }[] = [];

  if (whitespaceRatio <= 0.05)
    forCode.push({ weight: 2, reason: 'Almost no value contains a space.' });
  else if (whitespaceRatio >= 0.5)
    forName.push({ weight: 2, reason: `${percent(whitespaceRatio)} of values contain a space.` });

  if (meanTokens <= 1.2) forCode.push({ weight: 1, reason: 'Values are single words.' });
  else if (meanTokens >= 1.8)
    forName.push({ weight: 2, reason: `Values average ${meanTokens.toFixed(1)} words.` });

  if (digitRatio >= 0.3)
    forCode.push({ weight: 2, reason: `${percent(digitRatio)} of the characters are digits.` });
  else if (digitRatio <= 0.05)
    forName.push({ weight: 2, reason: 'Values are almost entirely letters.' });

  if (mixedRatio >= 0.5)
    forCode.push({ weight: 2, reason: `${percent(mixedRatio)} of values mix letters and digits.` });

  if (lengthVariation <= 0.15)
    forCode.push({ weight: 2, reason: 'Every value is close to the same length.' });
  else if (lengthVariation >= 0.35)
    forName.push({ weight: 1, reason: 'Value lengths vary widely.' });

  if (uniqueRatio >= 0.98) forCode.push({ weight: 1, reason: 'Nearly every value is unique.' });
  else if (uniqueRatio < 0.5)
    forName.push({
      weight: 1,
      reason: `Only ${distinct} distinct values across ${values.length} rows.`,
    });

  if (prefix.prefix !== null)
    forCode.push({
      weight: 2,
      reason: `${percent(prefix.share)} of values start with "${prefix.prefix}".`,
    });

  // A legal suffix is the single most direct evidence a column holds organisation
  // names, and it is the same evidence the name ladder normalises away.
  if (suffixRatio >= 0.05)
    forName.push({
      weight: 3,
      reason: `${percent(suffixRatio)} of values end in Inc, LLC, Ltd or Corp.`,
    });

  const codeScore = forCode.reduce((total, signal) => total + signal.weight, 0);
  const nameScore = forName.reduce((total, signal) => total + signal.weight, 0);
  // A dead heat is broken by whitespace: the one signal that separates the two
  // kinds without depending on the alphabet a customer uses.
  const proposal =
    codeScore > nameScore || (codeScore === nameScore && whitespaceRatio < 0.5)
      ? VALUE_KIND_CODE
      : VALUE_KIND_NAME;
  const total = codeScore + nameScore;
  const confidence = total <= 0 ? 0 : Math.abs(codeScore - nameScore) / total;

  const supporting = proposal === VALUE_KIND_CODE ? forCode : forName;
  const contrary = proposal === VALUE_KIND_CODE ? forName : forCode;
  const bySignificance = (left: { weight: number }, right: { weight: number }) =>
    right.weight - left.weight;
  return {
    column,
    proposedValueKind: proposal,
    confidence: Math.round(confidence * 1000) / 1000,
    reasons: [...supporting].sort(bySignificance).map((signal) => signal.reason),
    contrary: [...contrary].sort(bySignificance).map((signal) => signal.reason),
    profiledRowCount: sampledValues.length,
    nonEmptyCount: values.length,
    distinctCount: distinct,
    meanTokenCount: meanTokens,
    whitespaceRatio,
    digitRatio,
    lengthVariation,
    uniqueRatio,
    sharedPrefix: prefix.prefix,
  };
}

/**
 * What one typed value looks like, judged by the same signals as a column.
 *
 * One value cannot carry cardinality or length variance, so this reads only the
 * three signals a single string does carry. It is used to tell an operator that
 * what they typed is shaped like the wrong column, never to decide a match.
 */
export function shapeOfValue(value: string): { kind: string; description: string } | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const characters = [...trimmed];
  const digits = characters.filter(isDigit).length / characters.length;
  const hasSpace = /\s/.test(trimmed);
  const mixed = characters.some(isLetter) && characters.some(isDigit);
  if (mixed && !hasSpace)
    return { kind: VALUE_KIND_CODE, description: 'one word mixing letters and digits' };
  if (digits >= 0.5) return { kind: VALUE_KIND_CODE, description: 'mostly digits' };
  if (hasSpace && digits <= 0.05)
    return { kind: VALUE_KIND_NAME, description: 'several words of letters' };
  if (!hasSpace && digits === 0 && trimmed.length <= 12) return null;
  if (digits <= 0.05)
    return { kind: VALUE_KIND_NAME, description: 'letters, with no digits in it' };
  return null;
}

export const describeValueKind = (valueKind: string): string =>
  normalizeValueKind(valueKind) === VALUE_KIND_CODE ? 'codes and identifiers' : 'names and text';

// -----------------------------------------------------------------------------
// Why a reading found no row
// -----------------------------------------------------------------------------

/** `switchId`: the turned-off spelling switch (STRICT_SWITCH) that would make the reading match. */
export type NoMatchFinding = { severity: 'lead' | 'detail'; text: string; switchId?: string };

/**
 * What the character-confusion rung would really do with a reading that folds
 * onto a row, mirroring `LookupMatcher.Resolve`.
 *
 * The advice used to be "these two differ only in characters OCR confuses, so
 * switch the rung on" the moment the closest row folded together with the typed
 * value — which promises a match the engine then refuses, twice over. It groups
 * every row the fold reached by the value that row would write into the field and
 * refuses outright when two groups survive, because nothing in the reading says
 * which vendor was meant; and it answers unmatched when the row it settles on has
 * nothing in the column the rule fills from. Both refusals are the reason the rung
 * is safe enough to offer at all, so the offer has to respect them.
 *
 * The literal tie-break is the engine's: for codes `StrictKey` is `NormalizeCode`,
 * so a reading that lands on exactly one of the answers letter for letter breaks
 * the tie the way the engine breaks it.
 *
 * A row whose return cell is empty is not one of the rows that disagree, and this
 * copy used to let its emptiness be a rival answer in its own right — the exact
 * defect `LookupMatcher.Resolve` states it was fixed for. Three rows spelling one
 * company three ways and all returning the same code, beside one branch-office row
 * whose code cell the file leaves blank, came back as "2 rows fold onto this
 * reading and they return different values (“Ademero”, “”)": an empty string
 * quoted to the operator as a rival vendor, and a rung told to be useless that the
 * engine would have climbed to fill the field correctly. The rows that can answer
 * are what is grouped; a fold all of whose rows are empty says so instead.
 */
function confusionVerdict(input: {
  rows: string[][];
  findIndex: number;
  returnOf: (rowIndex: number) => string | null;
  returnColumn: string;
  typedFolded: string;
  typedKey: string;
  kind: string;
  rule: LookupRuleDefinition;
}): NoMatchFinding {
  const { rows, findIndex, returnOf, returnColumn, typedFolded, typedKey, kind, rule } = input;
  const wouldMatch: NoMatchFinding = {
    severity: 'detail',
    text: 'The two differ only in characters OCR confuses. Allowing for those below would match this reading.',
  };
  const groups = new Map<string, { returned: string; row: number; literal: boolean }>();
  let firstEmptyRow = 0;
  let folded = 0;
  for (let row = 0; row < rows.length; row += 1) {
    const cell = (rows[row][findIndex] ?? '').trim();
    if (cell.length === 0 || confusionKey(cell, kind) !== typedFolded) continue;
    const returned = returnOf(row);
    // The return column is not in what was read, so neither refusal can be judged
    // and the plain advice stands rather than a guess about them.
    if (returned === null) return wouldMatch;
    folded += 1;
    // Filtered out before the grouping, the way the engine filters it: a row with
    // no answer has no answer to disagree with.
    if (returned.length === 0) {
      if (firstEmptyRow === 0) firstEmptyRow = row + 1;
      continue;
    }
    // Grouped the way `StringComparer.OrdinalIgnoreCase` groups, which is the
    // simple case mapping and not JavaScript's fuller one.
    const key = toUpperInvariant(returned);
    const literal = exactKey(cell, kind) === typedKey;
    const held = groups.get(key);
    if (held) held.literal = held.literal || literal;
    else groups.set(key, { returned, row: row + 1, literal });
  }
  // Every row the fold reached is one the file has nothing to return from, which is
  // what the engine answers rather than climbing past it.
  if (folded > 0 && groups.size === 0) {
    return {
      severity: 'detail',
      text: `Allowing for those below would reach row ${firstEmptyRow.toLocaleString()}, which has no value in “${returnColumn}” - so the field would still come out empty.`,
    };
  }
  if (groups.size === 0) return wouldMatch;

  if (groups.size > 1) {
    const literal = [...groups.values()].filter((group) => group.literal);
    if (literal.length !== 1) {
      return {
        severity: 'detail',
        text: `Allowing for those below would not match it: ${groups.size} rows fold onto this reading and they return different values (${[
          ...groups.values(),
        ]
          .map((group) => `“${group.returned}”`)
          .slice(0, 3)
          .join(', ')}), so the rung refuses rather than pick one.`,
      };
    }
  }
  return wouldMatch;
}

/**
 * What the fuzzy rung would really do with the rows that were read, mirroring
 * `LookupFuzzyScan`.
 *
 * The advice used to be offered on the score floor alone, so a rule with a
 * runner-up margin was told a reading would match when the engine was about to
 * refuse it as a tie. Two guards are missing from a bare floor comparison. Rows
 * whose return cell is blank never reach the scan at all — the rung only scores
 * rows it could fill the field from. And the margin is read against the best row
 * returning a *different* value, because a directory listing one vendor twice is
 * not a tie while two vendors equally close is exactly one.
 */
function fuzzyVerdict(input: {
  rows: string[][];
  findIndex: number;
  returnOf: (rowIndex: number) => string | null;
  typedKey: string;
  kind: string;
  closestRow: number;
  rule: LookupRuleDefinition;
}): NoMatchFinding | null {
  const { rows, findIndex, returnOf, typedKey, kind, closestRow, rule } = input;
  type Scored = { raw: string; row: number; score: number; answer: string };
  const scored: Scored[] = [];
  for (let row = 0; row < rows.length; row += 1) {
    const cell = (rows[row][findIndex] ?? '').trim();
    // Skipped on the key rather than on the raw cell, the way the scan skips it: a
    // placeholder like "---" or "(n/a)" has a cell and no key, and the engine never
    // scores it.
    const cellKey = exactKey(cell, kind);
    if (cellKey.length === 0) continue;
    const returned = returnOf(row);
    // A row the rule cannot fill the field from never reaches the scan.
    if (returned !== null && returned.length === 0) continue;
    scored.push({
      raw: cell,
      row: row + 1,
      score: jaroWinkler(typedKey, cellKey),
      // Grouped the way the scan groups, which is `OrdinalIgnoreCase` — the simple
      // case mapping, not JavaScript's fuller one. `toUpperCase` merged answers the
      // engine keeps apart, so a genuine tie came back as a single group and the
      // pane promised a match the engine was about to refuse as ambiguous.
      answer: toUpperInvariant(returned ?? cell),
    });
  }
  // Ties go to the earlier row, the way the engine's own ranking breaks them.
  const ahead = (candidate: Scored, holder: Scored) =>
    candidate.score > holder.score ||
    (candidate.score === holder.score && candidate.row < holder.row);
  const best = scored.reduce<Scored | null>(
    (held, candidate) => (held === null || ahead(candidate, held) ? candidate : held),
    null,
  );
  if (best === null || best.score < rule.matchLadder.minimumScore) return null;
  const runnerUp = scored
    .filter((candidate) => candidate.answer !== best.answer)
    .reduce<Scored | null>(
      (held, candidate) => (held === null || ahead(candidate, held) ? candidate : held),
      null,
    );

  const alike =
    best.row === closestRow
      ? `They are ${percent(best.score)} alike.`
      : `The closest row it could fill the field from is “${best.raw}” at row ${best.row.toLocaleString()}, ${percent(best.score)} alike.`;
  if (runnerUp !== null && best.score - runnerUp.score < rule.matchLadder.runnerUpMargin) {
    return {
      severity: 'detail',
      text: `${alike} Allowing close spellings below would still not match it: “${runnerUp.raw}” at row ${runnerUp.row.toLocaleString()} is ${percent(runnerUp.score)} alike, and this rule requires the best to be ahead of the next-best by ${percent(rule.matchLadder.runnerUpMargin)} before it will pick one.`,
    };
  }
  return {
    severity: 'detail',
    text: `${alike} Allowing close spellings below would match this reading.`,
  };
}

/**
 * Why a value the operator typed found nothing, read out of the table's own rows.
 *
 * The engine answers "did this match", correctly, in one sentence. An operator
 * staring at "No row in the table matched this value" is then left to work out
 * whether they typed a vendor name at a rule that matches numbers, whether the
 * row is there under a spelling one glyph away, or whether the table simply does
 * not hold it — three different problems with three different fixes. So the rows
 * that were already fetched to preview the table are searched for the value, and
 * the answer names which of the three it is.
 */
export function diagnoseNoMatch(input: {
  value: string;
  rule: LookupRuleDefinition;
  columnNames: string[];
  rows: string[][];
  scannedRowCount: number;
  totalRowCount: number;
  findProfile: DataTableColumnProfile | null;
}): NoMatchFinding[] {
  const { value, rule, columnNames, rows, scannedRowCount, totalRowCount, findProfile } = input;
  const kind = normalizeValueKind(rule.valueKind);
  const findings: NoMatchFinding[] = [];
  const findIndex = columnNames.findIndex(
    (column) => column.toLowerCase() === rule.findColumn.toLowerCase(),
  );
  // The first field the rule fills stands for the row: a row with nothing in it is passed over.
  const returnColumn = rule.returns?.[0]?.column ?? rule.findColumn;
  const returnIndex = columnNames.findIndex(
    (column) => column.toLowerCase() === returnColumn.toLowerCase(),
  );
  /** What a row would write into the field, or null when the column was not read. */
  const returnOf = (rowIndex: number): string | null =>
    returnIndex < 0 ? null : (rows[rowIndex][returnIndex] ?? '').trim();
  const typedCodeKey = normalizeCode(value);
  const typedNameKey = normalizeName(value);
  if (typedCodeKey.length === 0 && typedNameKey.length === 0) return findings;

  // The strongest answer available: the value is in the file, in a column this
  // rule never looks at. Both key kinds are tried because the other column may
  // hold a different kind of value than the one being matched against.
  let elsewhere: { column: string; row: number } | null = null;
  for (let row = 0; row < rows.length && elsewhere === null; row += 1) {
    for (let column = 0; column < columnNames.length; column += 1) {
      if (column === findIndex) continue;
      const cell = rows[row][column] ?? '';
      if (cell.trim().length === 0) continue;
      if (normalizeCode(cell) === typedCodeKey || normalizeName(cell) === typedNameKey) {
        elsewhere = { column: columnNames[column], row: row + 1 };
        break;
      }
    }
  }
  if (elsewhere) {
    findings.push({
      severity: 'lead',
      text:
        elsewhere.column.toLowerCase() === returnColumn.toLowerCase() &&
        rule.findColumn !== returnColumn
          ? `“${value.trim()}” is in “${elsewhere.column}” at row ${elsewhere.row.toLocaleString()} - the column this rule fills in, not the one it matches against. Give it the “${rule.findColumn}” the document carries, or swap the two columns.`
          : `“${value.trim()}” is in “${elsewhere.column}” at row ${elsewhere.row.toLocaleString()}. This rule only matches against “${rule.findColumn}”.`,
    });
  }

  // The shape argument, which stands even when the value is nowhere in the file.
  const shape = shapeOfValue(value);
  const columnKind = findProfile ? normalizeValueKind(findProfile.proposedValueKind) : kind;
  if (shape && shape.kind !== columnKind) {
    findings.push({
      severity: elsewhere ? 'detail' : 'lead',
      text: `You typed ${shape.description}, and “${rule.findColumn}” holds ${describeValueKind(columnKind)}.`,
    });
  }

  // A rule with its strict switch off compares the strict keys, the way the engine does: with
  // Leading zeros off, "A1" is not "A01". A row the reading equals once that switch is on is
  // the whole answer, so it is said, naming the switch, rather than "nothing resembles it".
  const strict = readsStrictKey(rule);
  const ruleKey = (cell: string) => (strict ? strictKey(cell, kind) : exactKey(cell, kind));
  let switchMatch: { raw: string; row: number } | null = null;
  if (findIndex >= 0 && strict) {
    const forgivingTyped = exactKey(value, kind);
    for (let row = 0; row < rows.length && switchMatch === null; row += 1) {
      const cell = (rows[row][findIndex] ?? '').trim();
      const returned = returnOf(row);
      if (cell.length === 0 || (returned !== null && returned.length === 0)) continue;
      if (exactKey(cell, kind) === forgivingTyped) switchMatch = { raw: cell, row: row + 1 };
    }
  }
  if (switchMatch) {
    findings.push({
      severity: findings.length === 0 ? 'lead' : 'detail',
      text: `“${switchMatch.raw}” at row ${switchMatch.row.toLocaleString()} matches once ${STRICT_SWITCH[kind].label} is on.`,
      switchId: STRICT_SWITCH[kind].id,
    });
  }

  // The nearest row, and — when it is near enough that a rung would have caught
  // it — which rung, so the fix is a switch rather than a hunt.
  let best: { raw: string; row: number; score: number } | null = null;
  if (findIndex >= 0 && !switchMatch) {
    const typedKey = kind === VALUE_KIND_CODE ? typedCodeKey : typedNameKey;
    const typedRuleKey = ruleKey(value);
    const typedFolded = confusionKey(value, kind);
    for (let row = 0; row < rows.length; row += 1) {
      const cell = (rows[row][findIndex] ?? '').trim();
      if (cell.length === 0) continue;
      const score = jaroWinkler(typedRuleKey, ruleKey(cell));
      if (best === null || score > best.score) best = { raw: cell, row: row + 1, score };
    }
    if (best && best.score >= 0.8 && best.score < 1) {
      findings.push({
        severity: findings.length === 0 ? 'lead' : 'detail',
        text: `The closest value in “${rule.findColumn}” is “${best.raw}” at row ${best.row.toLocaleString()}.`,
      });
      // The row the reading is nearest to may be one the rule cannot answer with at
      // all, which is worth saying before any rung is suggested: it explains a value
      // that is plainly in the column and still unmatchable.
      const closestReturn = returnOf(best.row - 1);
      if (closestReturn !== null && closestReturn.length === 0) {
        findings.push({
          severity: 'detail',
          text: `That row has no value in “${returnColumn}”, so the rule passes over it - a row it cannot fill the field from is not a match however closely it reads.`,
        });
      }
      // Nothing below may promise a match the engine is going to refuse, so each rung
      // is offered only after the guards it would actually run have been applied.
      const foldsTogether = confusionKey(best.raw, kind) === typedFolded;
      if (kind === VALUE_KIND_CODE && foldsTogether && !rule.matchLadder.characterConfusion) {
        findings.push(
          confusionVerdict({
            rows,
            findIndex,
            returnOf,
            returnColumn,
            typedFolded,
            typedKey,
            kind,
            rule,
          }),
        );
      } else if (kind === VALUE_KIND_NAME && !rule.matchLadder.fuzzy) {
        const verdict = fuzzyVerdict({
          rows,
          findIndex,
          returnOf,
          typedKey,
          kind,
          closestRow: best.row,
          rule,
        });
        if (verdict) findings.push(verdict);
      } else if (
        kind === VALUE_KIND_NAME &&
        rule.matchLadder.fuzzy &&
        best.score < rule.matchLadder.minimumScore
      ) {
        findings.push({
          severity: 'detail',
          text: `They are ${percent(best.score)} alike, under the ${percent(rule.matchLadder.minimumScore)} this rule requires before it trusts a close spelling.`,
        });
      }
    }
  }

  if (findings.length === 0) {
    findings.push({
      severity: 'lead',
      text:
        scannedRowCount >= totalRowCount
          ? `Nothing in this table's ${totalRowCount.toLocaleString()} rows resembles that value, in any column.`
          : 'Nothing resembling that value was found in the rows searched, in any column.',
    });
  }
  if (scannedRowCount < totalRowCount) {
    findings.push({
      severity: 'detail',
      text: `Searched the first ${scannedRowCount.toLocaleString()} of ${totalRowCount.toLocaleString()} rows. The engine's own match ran against all of them.`,
    });
  }
  return findings;
}
